// @givmo/app-charity — the profile editor's BODY as a panel (PE-WEB1: the Profile page's Details tab).
//
// The live CPA-WEB6 editor (`profile-page.tsx`) is mission / website / contact + logo + the read-only
// verification domain, wrapped in its own page frame. The Profile page (an org-level page with three
// in-page tabs, D-PE-12) re-homes that editor as its first tab — SAME operations
// (`charityAdminGetCharityProfile` / `charityAdminUpdateCharityProfile` /
// `charityAdminUploadCharityProfileLogo`), SAME Owner gate, SAME domain guard, SAME testids — with the
// page frame owned by the page. This panel is that body, frame-less; every gate below is the editor's,
// verbatim, so the two render identically. Both the live route (`profile-page.tsx`) and the page's
// Details tab render this panel: its root `<div>` (`profile-facts` / `profile-facts-readonly`) is the
// ONE wrapper element the live route's DOM gains over the pre-move editor — the gates, ids, states
// and pixels are unchanged (the transplant is a MOVE, D-PE-5).

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { charityAdminGetCharityProfileOptions } from '@givmo/api-client/query';
import { isGivmoError, type CharityProfileOut } from '@givmo/api-client';
import { Button, Card, EmptyState, Icon, Skeleton, testIdProp } from '@givmo/design-system';
import { Building2, ShieldAlert } from 'lucide-react';
import { isOwnerRole } from '../admins/admins-roles';
import { membershipsQueryOptions, useActiveOrg } from '../../org/active-org-context';
import { DisplayNameCard } from './display-name-card';
import { DomainGuard } from './domain-guard';
import { LogoUploader } from './logo-uploader';
import { ProfileForm } from './profile-form';
import { ProfileReadonlyView } from './profile-readonly-view';
import { isHumanEdited, provenanceByField } from './profile-provenance';
import { EDITABLE_FIELDS, provenanceFieldFor, type EditableField } from './profile-schemas';

const PREFIX = 'profile';

export function ProfileFactsPanel() {
  const queryClient = useQueryClient();
  const {
    activeMembership: membership,
    activeCharityId: charityId,
    isPending,
    isError,
    error,
    refetch,
  } = useActiveOrg();
  // Global Admin gate — FAIL CLOSED: the editable affordances render ONLY once the caller positively
  // holds the ACTIVE org's org-level role (the wire's `Owner`, which the console calls Global Admin).
  const canEdit = membership !== null && isOwnerRole(membership.role);

  const profileOptions = charityAdminGetCharityProfileOptions({
    path: { charity_id: charityId ?? '' },
  });
  const profileQuery = useQuery({ ...profileOptions, enabled: charityId != null });
  const profile: CharityProfileOut | null = profileQuery.data?.profile ?? null;

  // Keyed by FORM field, looked up by LEDGER field — the two names differ for `website`, which
  // writes `organization_domain` (CPA-WEB74). Filtering on the form name alone meant the Website
  // hint could never fire. A form field with no ledger field (the two contact fields, CPA-WEB75)
  // is never in the set, whatever a row on the wire claims.
  const humanFields = useMemo<ReadonlySet<EditableField>>(() => {
    const byField = provenanceByField(profile?.provenance);
    return new Set(
      EDITABLE_FIELDS.filter((f) => {
        const ledgerField = provenanceFieldFor(f);
        return ledgerField !== null && isHumanEdited(byField, ledgerField);
      }),
    );
  }, [profile]);

  const invalidateProfile = () =>
    queryClient.invalidateQueries({ queryKey: profileOptions.queryKey });
  // A display name is `name` everywhere the console reads it: the profile, and the memberships its
  // header and organization switcher name (CPA-BE44).
  const invalidateNames = () =>
    Promise.all([
      invalidateProfile(),
      queryClient.invalidateQueries({ queryKey: membershipsQueryOptions().queryKey }),
    ]);

  if (isPending) {
    return (
      <Card className="p-24" testId={`${PREFIX}-loading`}>
        <div className="flex flex-col gap-10">
          <Skeleton className="h-16 w-1/3" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <EmptyState
        testId={`${PREFIX}-memberships-error`}
        icon={<Icon icon={Building2} size={20} aria-hidden />}
        title="Couldn’t load your organization"
        description={
          isGivmoError(error)
            ? error.message
            : 'We couldn’t load your organization. Please try again.'
        }
        action={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={refetch}
            testId={`${PREFIX}-memberships-retry`}
          >
            Try again
          </Button>
        }
      />
    );
  }

  if (!membership || !charityId) {
    return (
      <EmptyState
        testId={`${PREFIX}-no-org`}
        icon={<Icon icon={Building2} size={20} aria-hidden />}
        title="Claim your organization first"
        description="Once your organization is verified you can edit its public profile here."
      />
    );
  }

  if (profileQuery.isPending) {
    return (
      <Card className="p-24" testId={`${PREFIX}-profile-loading`}>
        <div className="flex flex-col gap-10">
          <Skeleton className="h-16 w-1/3" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      </Card>
    );
  }

  if (profileQuery.isError || !profile) {
    return (
      <EmptyState
        testId={`${PREFIX}-profile-error`}
        icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
        title="Couldn’t load your profile"
        description={
          isGivmoError(profileQuery.error)
            ? profileQuery.error.message
            : 'We couldn’t load your organization’s profile. Please try again.'
        }
        action={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void profileQuery.refetch()}
            testId={`${PREFIX}-profile-retry`}
          >
            Try again
          </Button>
        }
      />
    );
  }

  // The "Name on Givmo" card (CPA-BE44): only while the profile carries its block, which the backend
  // serves only with CHARITY_DISPLAY_NAME_ENABLED on, so with it off nothing here changes.
  const displayName = profile.display_name ? (
    <DisplayNameCard
      charityId={charityId}
      name={profile.name}
      block={profile.display_name}
      canEdit={canEdit}
      onChanged={invalidateNames}
    />
  ) : null;

  if (!canEdit) {
    return (
      <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-facts-readonly`)}>
        <ProfileReadonlyView profile={profile} />
        {displayName}
        <DomainGuard domain={profile.organization_domain} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-facts`)}>
      {displayName}
      <Card className="p-24" testId={`${PREFIX}-logo-card`}>
        <LogoUploader
          charityId={charityId}
          orgName={profile.name}
          logoUrl={profile.logo_url}
          onUploaded={invalidateProfile}
        />
      </Card>
      <ProfileForm
        charityId={charityId}
        profile={profile}
        humanFields={humanFields}
        onSaved={invalidateProfile}
      />
      <DomainGuard domain={profile.organization_domain} />
    </div>
  );
}
